'use client';
import { useMemo, useState, useRef } from 'react';
import { FOUNDATION_CLIENT_ID_LONG } from 'components/UI/FoundationPlayer/config';
import FoundationPlayer from 'components/UI/FoundationPlayer/FoundationPlayer';
import { Icon } from 'components/UI/Icon';
import Image from 'components/UI/Image/Image';
import { Title } from 'components/UI/Title';
import type { VideoProps } from 'types/videoTypes';
import { formatSecondsToMinutes, getDateFormat } from 'utils/time';
import { EpgVideoFactory } from 'utils/video';
import { getVideoDuration } from 'utils/videoPlayer';
import type { WidgetEpgDefaultProps } from '../default';
import styles from './longform.module.scss';

const ITEMS_PER_PAGE = 5;

export const WidgetEpgLongform = ({ context }: { context: WidgetEpgDefaultProps }) => {
  const { headline } = context.model.fields;
  const playerRef = useRef<HTMLDivElement>(null);
  const initialPlaylist = useMemo(() => context.items.map((item) => EpgVideoFactory(item)), [context]);

  const [currentVideo, setCurrentVideo] = useState<VideoProps | undefined>(initialPlaylist?.[0] ?? undefined);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [showLoadMore, setShowLoadMore] = useState<boolean>(initialPlaylist?.length > ITEMS_PER_PAGE);
  const [currentStartType, setCurrentStartType] = useState<number>(0);
  const [currentAutoplay, setCurrentAutoplay] = useState<boolean>(false);

  const filteredPlaylist = useMemo(
    () => initialPlaylist?.slice(0, ITEMS_PER_PAGE * currentPage) ?? undefined,
    [initialPlaylist, currentPage]
  );

  function handleItemClick(e: React.MouseEvent<HTMLButtonElement>, index: number) {
    setCurrentStartType(2);
    setCurrentAutoplay(true);
    setCurrentVideo(filteredPlaylist?.[index]);
    setActiveIndex(index);
    setTimeout(() => {
      playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  function handleLoadMore() {
    setCurrentPage((prevPage) => {
      const nextPage = prevPage + 1;
      setShowLoadMore(initialPlaylist?.length > ITEMS_PER_PAGE * nextPage);
      return nextPage;
    });
  }

  return (
    <section aria-labelledby="epg-longform-heading">
      {!!headline && (
        <Title as="h2" id="epg-longform-heading">
          {headline}
        </Title>
      )}
      {currentVideo && (
        <div ref={playerRef} className={styles.player}>
          <FoundationPlayer
            video={currentVideo}
            clientIdentifier={FOUNDATION_CLIENT_ID_LONG}
            autoplay={currentAutoplay}
            startType={currentStartType}
            postRollDisabled
          />
        </div>
      )}

      {filteredPlaylist && (
        <nav aria-label="Playlist">
          <ul aria-live="polite" className={styles.list}>
            {filteredPlaylist?.map((video, index) => {
              return (
                <li key={video.id}>
                  <button
                    aria-label={`Video abspielen: ${video.fields.title}`}
                    aria-current={index === activeIndex ? 'true' : undefined}
                    onClick={(e) => handleItemClick(e, index)}
                    className={styles.item}
                  >
                    <div className={styles.imageWrapper}>
                      <Image image={video?.image} alt={video.fields.title} className={styles.image} />
                      <span className={styles.duration}>
                        <Icon name="play" size={16} />{' '}
                        {index === activeIndex ? 'Läuft gerade' : formatSecondsToMinutes(getVideoDuration(video))}
                      </span>
                    </div>
                    <div>
                      <strong className={styles.title}>{video.fields.title}</strong>
                      <p className={styles.meta}>
                        {video?.fields?.caption && <span className={styles.ut}>UT</span>}
                        {video?.fields?.episodeNumber && `Folge ${video?.fields?.episodeNumber} • `}
                        {video?.firstAiredDate && getDateFormat(video.firstAiredDate, true)}
                      </p>
                      <p>{video?.fields?.description}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      )}

      {showLoadMore && (
        <button aria-label="Mehr Folgen laden" className={styles.loadMore} onClick={handleLoadMore}>
          Mehr Folgen laden
        </button>
      )}
    </section>
  );
};
